import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/common/modal';
import { findLegalDocument } from '@/components/legal/legal-documents';
import { supportedLocales, type SupportedLocales } from '@/i18n/i18n';

function resolveLegalLocale(language: string): SupportedLocales {
  const base = language.substring(0, 2);
  if ((supportedLocales as readonly string[]).includes(base)) {
    return base as SupportedLocales;
  }
  return 'en';
}

export type LegalDocumentSlug = 'terms' | 'privacy' | 'cookies';

type Props = {
  open: boolean;
  slug: LegalDocumentSlug | null;
  onClose: () => void;
};

/**
 * Overlay for BRITESTAGE legal documents (terms / privacy / cookies).
 * Intended for use on top of another modal (e.g. checkout) so closing returns to that flow.
 */
export default function LegalDocumentModal({ open, slug, onClose }: Props) {
  const { i18n } = useTranslation();
  const lang = resolveLegalLocale(i18n.language);
  const document = slug ? findLegalDocument(slug) : undefined;
  const DocumentComponent = document?.component[lang];

  return (
    <Modal
      open={open && !!document}
      handleClose={onClose}
      title={document?.title[lang] ?? ''}
      size="lg"
      width="820px"
      height="min(80vh, 720px)"
      maxHeight="calc(100vh - 64px)"
      contentSx={{ overflow: 'hidden', pb: 2 }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          pr: 1,
        }}
      >
        {DocumentComponent ? <DocumentComponent /> : null}
      </Box>
    </Modal>
  );
}
